export interface MiloSupportWidgetEnvironment {
    VITE_API_BASE_URL: string;
    VITE_MILO_SUPPORT_WIDGET_ENDPOINT?: string;
    VITE_MILO_SUPPORT_WIDGET_SRC?: string;
    VITE_MILO_SUPPORT_WIDGET_TELEMETRY_ENDPOINT?: string;
    VITE_MILO_SUPPORT_WIDGET_TRANSCRIBE_ENDPOINT?: string;
}

export interface MiloSupportWidgetConfig {
    endpoint: string;
    src: string;
    telemetryEndpoint: string;
    transcribeEndpoint: string;
}

/**
 * Pinned by version because the assets are immutable: a changed bundle always
 * gets a new version rather than replacing an existing one. v0.7.0 is the first
 * with the in-app handoff CTA and the feedback form (MILO-501); on v0.6.4 the
 * feedback button simply is not there.
 */
export const DEFAULT_MILO_SUPPORT_WIDGET_SRC = 'https://assets.milomoves.app/widget.v0.8.9.js';

/**
 * Footprint of the widget's floating launcher, mirrored from the widget source
 * (milo-ai, `apps/support-chat/src/widget/element.ts`, rule `.launcher`): a
 * 62px circle inset 22px from the bottom-right corner. Keep these in step with
 * that rule — they are what the app reserves for the button.
 */
export const MILO_LAUNCHER_SIZE = 62;
export const MILO_LAUNCHER_INSET = 22;

/** Breathing room between the launcher and whatever the page ends with. */
export const MILO_LAUNCHER_CLEARANCE = 12;

/**
 * Custom property the layouts reserve as trailing space. Set by
 * MiloSupportWidget while the launcher is on the page, cleared when it is not,
 * so a build without the widget wastes no room. See docs/SUPPORT_WIDGET.md.
 */
export const MILO_LAUNCHER_SAFE_AREA_PROPERTY = '--milo-launcher-safe-area';

/**
 * The space a page must keep free at its end so the launcher never comes to
 * rest on top of it. On devices with a home indicator the launcher sits above
 * the inset, so the reserved area has to clear that too.
 */
export function miloLauncherSafeArea(): string {
    const reserved = MILO_LAUNCHER_SIZE + MILO_LAUNCHER_INSET + MILO_LAUNCHER_CLEARANCE;

    return `calc(${reserved}px + env(safe-area-inset-bottom, 0px))`;
}

export function resolveMiloSupportWidgetConfig(environment: MiloSupportWidgetEnvironment): MiloSupportWidgetConfig {
    const apiBaseUrl = environment.VITE_API_BASE_URL.replace(/\/$/, '');

    return {
        endpoint: environment.VITE_MILO_SUPPORT_WIDGET_ENDPOINT ?? `${apiBaseUrl}/api/ai/chat`,
        src: environment.VITE_MILO_SUPPORT_WIDGET_SRC ?? DEFAULT_MILO_SUPPORT_WIDGET_SRC,
        telemetryEndpoint: environment.VITE_MILO_SUPPORT_WIDGET_TELEMETRY_ENDPOINT ?? `${apiBaseUrl}/api/ai/events`,
        transcribeEndpoint:
            environment.VITE_MILO_SUPPORT_WIDGET_TRANSCRIBE_ENDPOINT ?? `${apiBaseUrl}/api/ai/transcribe`,
    };
}
